import { Progress } from '@mantine/core';
import { IconCircle, IconCircleCheckFilled } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';

import styles from './OnboardingChecklist.module.scss';
import { Props } from './OnboardingChecklist.types';

const ONBOARDING_STEP_PATHS: Record<string, (assetId: string) => string> = {
  'practical-information': assetId => `/asset/${assetId}/infos/practical-info`,
  planning: assetId => `/asset/${assetId}/planning`,
  community: assetId => `/asset/${assetId}/community`,
  expenses: assetId => `/asset/${assetId}/expenses`,
  settings: assetId => `/asset/${assetId}/admin`,
  checklists: assetId => `/asset/${assetId}/guides/checklists`,
};

const OnboardingChecklist = ({ steps, completionPercent }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const assetId = useCurrentAssetId();

  if (completionPercent >= 100) return null;

  const handleStepClick = (linkTo: string) => {
    if (!assetId) return;
    const resolvePath = ONBOARDING_STEP_PATHS[linkTo];
    navigate(resolvePath ? resolvePath(assetId) : `/asset/${assetId}/${linkTo}`);
  };

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>
        {t('dashboard.onboarding.title', 'Bien démarrer')} ({completionPercent}%)
      </h3>

      <Progress value={completionPercent} color="teal" size="sm" className={styles.progress} />

      <ul className={styles.list}>
        {steps.map(step =>
          step.done ? (
            <li key={step.key} className={`${styles.row} ${styles.done}`}>
              <IconCircleCheckFilled size={20} className={styles.iconDone} />
              <span className={styles.label}>{step.label}</span>
            </li>
          ) : (
            <li key={step.key}>
              <button
                type="button"
                className={styles.row}
                onClick={() => handleStepClick(step.linkTo)}
              >
                <IconCircle size={20} className={styles.iconTodo} />
                <span className={styles.label}>{step.label}</span>
              </button>
            </li>
          )
        )}
      </ul>
    </div>
  );
};

export default OnboardingChecklist;
